import { ApolloQueryResult, OperationVariables, gql } from '@apollo/client';
import { maybeRestErrorMessage } from 'modules/campaigns/editor/utils';
import { DEFAULT_RESPONSE_ERROR_MESSAGE } from 'modules/campaigns/lib/constants';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useGraphql } from 'utils/hooks/useGraphql';
import { WebhookAuthType } from '../types';

export const getWebhooksQuery = gql`
  query campaignAlertsWebhooks {
    campaignAlertsWebhooks {
      webhooks {
        id
        createdAt
        updatedAt
        projectId
        name
        enabled
        endpoint
        authType
        authToken
        clientId
        clientSecret
        tokenEndpoint
      }
    }
  }
`;

export type CampaignAlertsWebhooks = {
  id: number;
  createdAt: string;
  updatedAt: string;
  projectId: number;
  name: string;
  enabled: boolean;
  endpoint: string;
  authType: WebhookAuthType;
  authToken?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  tokenEndpoint?: string | null;
}[];

export type GetWebhooksState = {
  webhooks: CampaignAlertsWebhooks;
  loading: boolean;
  errorMessage?: string;
  refetch: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<ApolloQueryResult<CampaignAlertsWebhooks>>;
};

export const useGetWebhooks = (): GetWebhooksState => {
  const dispatch = useDispatch();

  const {
    data = {},
    isLoading,
    errorMessage: error,
    refetch,
  } = useGraphql(getWebhooksQuery, {
    onError: (e) => {
      const errorResponseMessage = maybeRestErrorMessage(e) || DEFAULT_RESPONSE_ERROR_MESSAGE;
      const errorMessage = `Cannot fetch webhooks: ${errorResponseMessage}`;
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useGetWebhooks', e));
    },
  });

  return {
    webhooks: data?.campaignAlertsWebhooks?.webhooks,
    loading: isLoading,
    errorMessage: error?.message || data?.campaignAlertsWebhooks?.failure?.message,
    refetch,
  };
};
